import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, tap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import {
  ClientSignInGQL,
  ClientMeGQL,
  ClientUserRole,
  MyProfile,
} from '../../graphql/generated';

@Injectable({
  providedIn: 'root',
})
export class ClientAuthService {
  private readonly router = inject(Router);
  private readonly signInGQL = inject(ClientSignInGQL);
  private readonly meGQL = inject(ClientMeGQL);

  readonly currentUser = signal<MyProfile | null>(null);
  readonly isAuthenticated = computed(() => !!this.currentUser());
  readonly isLoading = signal(false);

  /**
   * Sessions opened before roles existed have no `role` in the stored profile,
   * so this stays false until `checkSession()` refreshes it from the server.
   * Denying by default is the safe direction: the worst case is a client admin
   * briefly not seeing their own admin nav, which one reload fixes.
   */
  readonly isClientAdmin = computed(
    () => this.currentUser()?.role === ClientUserRole.ClientAdmin,
  );

  constructor() {
    this.restoreSession();
    this.checkSession();
  }

  /**
   * Hydrates the session synchronously so route guards evaluating
   * `isAuthenticated()` on a page reload do not bounce a valid session back to
   * the login screen while `checkSession()` is still in flight.
   */
  private restoreSession() {
    if (typeof window === 'undefined') return;
    const storedUser = localStorage.getItem('client_auth_user');
    if (storedUser) {
      try {
        this.currentUser.set(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse client_auth_user:', e);
      }
    }
  }

  signIn(email: string, password: string): Observable<boolean> {
    this.isLoading.set(true);
    return this.signInGQL.mutate({ 
      variables: { email, password },
      context: { clientName: 'client' }
    }).pipe(
      map(result => {
        const data = result.data?.clientSignIn;
        if (data?.user) {
          this.currentUser.set(data.user as MyProfile);
          localStorage.setItem('client_auth_token', data.token);
          localStorage.setItem('client_auth_user', JSON.stringify(data.user));
          return true;
        }
        return false;
      }),
      tap(() => this.isLoading.set(false)),
      catchError(error => {
        console.error('Client sign-in error:', error);
        this.isLoading.set(false);
        return of(false);
      })
    );
  }

  logout() {
    localStorage.removeItem('client_auth_token');
    localStorage.removeItem('client_auth_user');
    this.currentUser.set(null);
    this.router.navigate(['/client/login']);
  }

  private checkSession() {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('client_auth_token');
    if (!token) return;

    this.isLoading.set(true);
    this.meGQL.fetch({ 
      fetchPolicy: 'network-only',
      context: { clientName: 'client' }
    }).subscribe({
      next: (result) => {
        if (result.data?.clientMe) {
          this.currentUser.set(result.data.clientMe as MyProfile);
        } else {
          this.logout();
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.logout();
        this.isLoading.set(false);
      }
    });
  }
}
